import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarIcon from '@mui/icons-material/Star';

import { useTvMazeShowsQuery } from '../actions/tvmazeSlice';
import { useGetPopularMoviesQuery, useGetTopTvQuery } from '../actions/tmdbApiSlice';
import MediaRail from '../components/MediaRail';
import PosterCard from '../components/PosterCard';

const placeholderImageUrl = 'https://placehold.co/300x450.png?text=No+Poster';

function shuffleArray(array) {
  const a = array.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function HomeScreen() {
  const navigate = useNavigate();
  const [tvList, setTvList] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);

  const { data: tvData, error: tvError, isFetching: tvFetching } = useTvMazeShowsQuery();
  const { data: popularMoviesData, isFetching: popularFetching } = useGetPopularMoviesQuery();
  const { data: topTvData, isFetching: topTvFetching } = useGetTopTvQuery();
  const popularMovies = popularMoviesData?.results;
  const topTv = topTvData?.results;

  useEffect(() => {
    if (tvData) setTvList(shuffleArray(tvData));
  }, [tvData]);

  useEffect(() => {
    if (tvError) {
      toast.error(`${Object.values(tvError.data || { msg: 'Failed to load shows' })}`);
    }
  }, [tvError]);

  const heroPool = useMemo(() => {
    if (!popularMovies) return [];
    return popularMovies.filter((m) => m.backdrop_path).slice(0, 5);
  }, [popularMovies]);

  useEffect(() => {
    if (heroPool.length < 2) return;
    const id = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroPool.length);
    }, 7000);
    return () => clearInterval(id);
  }, [heroPool.length]);

  const hero = heroPool[heroIndex];

  const navigateToShow = (item) => {
    const year = item.premiered
      ? item.premiered.split('-')[0]
      : item.release_date
      ? item.release_date.split('-')[0]
      : item.first_air_date
      ? item.first_air_date.split('-')[0]
      : '';
    const nameYear = `${item.name || item.title}${year ? ` ${year}` : ''}`;

    let idSuffix = '';
    if (item.externals?.imdb) {
      // TVmaze item: its `id` is a TVmaze id (NOT a TMDB id) — sending it as &mid
      // made the backend fetch an unrelated movie. Resolve by the IMDb id it
      // carries so we get the exact title, kind, details and cast.
      idSuffix = `&imdb=${item.externals.imdb}`;
    } else if (item.id) {
      // TMDB item (popular movies, recommendations); media_type picks movie vs tv.
      const idParam = item.media_type === 'tv' ? 'tid' : 'mid';
      idSuffix = `&${idParam}=${item.id}`;
    }
    navigate(`/details/?q=${encodeURIComponent(nameYear)}${idSuffix}`);
  };

  return (
    <>
      <Helmet>
        <title>AirCheck | Track every show, never miss an episode</title>
      </Helmet>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 360, sm: 460, md: 560 },
          overflow: 'hidden',
          mb: { xs: 3, md: 5 },
        }}
      >
        {hero ? (
          <>
            <Box
              key={hero.id}
              component="img"
              src={`https://image.tmdb.org/t/p/original${hero.backdrop_path}`}
              alt={hero.title || hero.name}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                animation: 'heroFade 1.2s ease',
                '@keyframes heroFade': {
                  from: { opacity: 0, transform: 'scale(1.04)' },
                  to: { opacity: 1, transform: 'scale(1)' },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: (t) =>
                  t.palette.mode === 'dark'
                    ? 'linear-gradient(180deg, rgba(10,10,15,0.4) 0%, rgba(10,10,15,0.55) 50%, rgba(10,10,15,1) 100%), linear-gradient(90deg, rgba(10,10,15,0.85) 0%, rgba(10,10,15,0) 60%)'
                    : 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(250,250,251,0.6) 60%, rgba(250,250,251,1) 100%), linear-gradient(90deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 60%)',
              }}
            />
            <Container
              maxWidth="xl"
              sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'flex-end', pb: { xs: 4, md: 6 } }}
            >
              <Box sx={{ maxWidth: 640 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <Chip label="Featured" color="primary" size="small" />
                  {hero.vote_average ? (
                    <Chip
                      size="small"
                      icon={<StarIcon sx={{ fontSize: 16 }} />}
                      label={hero.vote_average.toFixed(1)}
                    />
                  ) : null}
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: 32, sm: 44, md: 60 },
                    fontWeight: 900,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.05,
                    mb: 1.5,
                  }}
                >
                  {hero.title || hero.name}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: 3,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {hero.overview}
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => navigateToShow(hero)}
                  >
                    Watch
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="large"
                    startIcon={<InfoOutlinedIcon />}
                    onClick={() => navigateToShow(hero)}
                    sx={{ borderColor: (t) => t.palette.divider }}
                  >
                    More info
                  </Button>
                </Stack>
              </Box>
            </Container>
          </>
        ) : (
          <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} animation="wave" />
        )}
      </Box>

      <Container maxWidth="xl" sx={{ pb: 6 }}>
        <MediaRail title="Top picks for you">
          {topTvFetching || !topTv
            ? Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} sx={{ flex: '0 0 auto', width: 170 }}>
                  <Skeleton
                    variant="rounded"
                    sx={{ width: '100%', aspectRatio: '2 / 3', borderRadius: 2.5 }}
                  />
                  <Skeleton sx={{ mt: 1, width: '80%' }} />
                </Box>
              ))
            : topTv.map((item) => (
                <PosterCard
                  key={item.id}
                  src={
                    item.poster_path
                      ? `https://image.tmdb.org/t/p/w342${item.poster_path}`
                      : placeholderImageUrl
                  }
                  title={item.name || item.title}
                  subtitle={item.first_air_date ? item.first_air_date.split('-')[0] : ''}
                  badge={
                    item.vote_average ? `★ ${item.vote_average.toFixed(1)}` : undefined
                  }
                  onClick={() => navigateToShow({ ...item, media_type: 'tv' })}
                />
              ))}
        </MediaRail>

        <MediaRail title="Popular movies">
          {popularFetching || !popularMovies
            ? Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} sx={{ flex: '0 0 auto', width: 170 }}>
                  <Skeleton
                    variant="rounded"
                    sx={{ width: '100%', aspectRatio: '2 / 3', borderRadius: 2.5 }}
                  />
                  <Skeleton sx={{ mt: 1, width: '80%' }} />
                </Box>
              ))
            : popularMovies.map((movie) => (
                <PosterCard
                  key={movie.id}
                  src={
                    movie.poster_path
                      ? `https://image.tmdb.org/t/p/w342${movie.poster_path}`
                      : placeholderImageUrl
                  }
                  title={movie.title || movie.name}
                  subtitle={
                    movie.release_date
                      ? movie.release_date.split('-')[0]
                      : movie.first_air_date
                      ? movie.first_air_date.split('-')[0]
                      : ''
                  }
                  badge={
                    movie.vote_average
                      ? `★ ${movie.vote_average.toFixed(1)}`
                      : undefined
                  }
                  onClick={() => navigateToShow({ ...movie, media_type: 'movie' })}
                />
              ))}
        </MediaRail>

        <MediaRail title="Classics">
          {tvFetching || tvList.length === 0
            ? Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} sx={{ flex: '0 0 auto', width: 170 }}>
                  <Skeleton
                    variant="rounded"
                    sx={{ width: '100%', aspectRatio: '2 / 3', borderRadius: 2.5 }}
                  />
                  <Skeleton sx={{ mt: 1, width: '80%' }} />
                </Box>
              ))
            : tvList.map((tvItem, index) => (
                <PosterCard
                  key={`${tvItem.id || index}-${tvItem.name}`}
                  src={tvItem.image?.original || tvItem.image?.medium || placeholderImageUrl}
                  title={tvItem.name}
                  subtitle={tvItem.premiered ? tvItem.premiered.split('-')[0] : ''}
                  onClick={() => navigateToShow(tvItem)}
                />
              ))}
        </MediaRail>
      </Container>
    </>
  );
}

export default HomeScreen;
